import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  RefreshCw,
  CheckCircle,
  Paperclip,
  Calendar,
  User,
  Check,
  Mail,
  MailOpen
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const StaffNotificationsPage = ({ onCountChange }) => {
  const { t, isRTL } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Poll every 10s while on this page (same as desktop Notifications page)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchNotifications({ silent: true });
    }, 10 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Update parent count when unreadCount changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(unreadCount);
    }
  }, [unreadCount, onCountChange]);

  const fetchNotifications = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/notifications`, {
        withCredentials: true
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/staff/notifications/${notificationId}/mark-read`, {}, {
        withCredentials: true
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.notification_id === notificationId 
            ? { ...n, is_read: true } 
            : n
        )
      );
      // Decrement count immediately
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.post(`${API}/staff/notifications/mark-all-read`, {}, {
        withCredentials: true
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      // Set count to 0
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper to get correct attachment URL (handles both old and new formats)
  const getAttachmentUrl = (attachmentUrl) => {
    if (!attachmentUrl) return null;
    // If URL already starts with /api, don't add API prefix again
    if (attachmentUrl.startsWith('/api/')) {
      return `${API}${attachmentUrl.substring(4)}`; // Remove /api from start
    }
    return `${API}${attachmentUrl}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-7 h-7 text-indigo-600" />
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white ml-2">
                {unreadCount} new
              </Badge>
            )}
          </h1>
          <p className="text-gray-600 mt-1">Messages from your company admin</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => fetchNotifications()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllAsRead}
              className="text-green-600 border-green-300 hover:bg-green-50"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle>All Notifications</CardTitle>
          <CardDescription>Click on a notification to mark it as read</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No notifications yet</h3>
              <p className="text-gray-500 mt-1">You&apos;ll see messages from your admin here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  onClick={() => !notif.is_read && handleMarkAsRead(notif.notification_id)}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    notif.is_read 
                      ? 'bg-white border-gray-200 hover:bg-gray-50' 
                      : 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                  }`}
                  data-testid={`notification-${notif.notification_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Read/Unread Icon */}
                    <div className={`p-2 rounded-full ${
                      notif.is_read ? 'bg-gray-100' : 'bg-indigo-100'
                    }`}>
                      {notif.is_read ? (
                        <MailOpen className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Mail className="w-5 h-5 text-indigo-600" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          From: {notif.sender_name}
                        </span>
                        {!notif.is_read && (
                          <Badge className="bg-indigo-600 text-white text-xs">New</Badge>
                        )}
                      </div>
                      <p className={`text-sm ${notif.is_read ? 'text-gray-600' : 'text-gray-800'}`}>
                        {notif.message}
                      </p>
                      
                      {/* Attachment */}
                      {notif.attachment_url && (
                        <a
                          href={getAttachmentUrl(notif.attachment_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 mt-2 text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          <Paperclip className="w-4 h-4" />
                          View Attachment
                        </a>
                      )}
                      
                      {/* Timestamp */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(notif.created_at)}
                        </span>
                        {notif.is_read && notif.read_at && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Check className="w-3 h-3" />
                            Read
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffNotificationsPage;
